/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import axios from 'axios'

import ImpersonateService from '@/services/Impersonate/ImpersonateService'

const formTypes = [
    { id: 1, name: 'Activé' },
    { id: 2, name: 'Désactivé' }
]
function prepareParams(config) {
    return new Promise((resolve, reject) => {
        var field = config.sort

        if (config.sortDirection === 'desc') {
            field = '-' + field
        }

        var params = {
            page: config.page,
            include: config.include,
            sort: field,
            per_page: config.per_page
        }

        if (config.filters) {
            for (const [key, value] of Object.entries(config.filters)) {
                params[`filter[${key}]`] = value
            }
        }

        resolve(params)
    })
}

export default {
    getCompanies({ state, commit }) {
        return new Promise((resolve, reject) => {
            prepareParams(state.companiesConfig)
                .then((params) => {
                    axios
                        .get(`${process.env.VUE_APP_BACKEND_URL}/api/companies`, { params })
                        .then((response) => {
                            const payload = response.data.companies || response.data
                            commit('setCompanies', payload)
                            if (response.data.statuses) {
                                commit('setStatuses', response.data.statuses)
                            }
                            resolve(payload)
                        })
                        .catch((error) => {
                            reject(error)
                        })
                })
        })
    },
    switchCompany({ commit }, company) {
        return new Promise((resolve) => {
            commit('auth/setCompany', company, { root: true })
            // initialiseStore rebuilds the baseURL from auth.company on boot,
            // so a full reload is the cleanest way to reset every scoped request.
            if (typeof window !== 'undefined') {
                window.location.href = '/'
            }
            resolve(company)
        })
    },
    updateCompanyActive({ commit }, { id, active }) {
        return new Promise((resolve, reject) => {
            axios
                .put(`${process.env.VUE_APP_BACKEND_URL}/api/companies/${id}/active`, { active: !!active })
                .then((response) => {
                    const updated = response.data.company
                    const nextActive = updated && typeof updated.active !== 'undefined' ? !!updated.active : !!active
                    commit('patchCompany', { id, active: nextActive })
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getCompany({ commit }, id) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${process.env.VUE_APP_BACKEND_URL}/api/companies/${id}`)
                .then((response) => {
                    commit('setCompany', response.data.company)
                    commit('setCompanyStats', response.data.stats || null)
                    commit('setCompanySettings', response.data.settings || null)
                    commit('setCompanyHeaderMail', response.data.header_mail || null)
                    if (response.data.statuses) {
                        commit('setStatuses', response.data.statuses)
                    }
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getCompaniesFilters({ state, rootState, commit }, id) {
        return new Promise((resolve, reject) => {
            commit('setCompaniesFilters', { formTypes })
            resolve()
        })
    },
    createCompany({ }, data) {
        return axios.post(`${process.env.VUE_APP_BACKEND_URL}/api/companies`, data)
    },
    updateCompany({ }, data) {
        return axios.put(`${process.env.VUE_APP_BACKEND_URL}/api/companies/${data.id}`, data)
    },
    deleteTemplate({ state, rootState, commit }, data) {
        return new Promise((resolve, reject) => {
            try {
                axios
                    .delete(`${rootState.baseURL}/response-template/${data.id}`)
                    .then((response) => {
                        commit('deleteTemplate', data)
                        resolve(response)
                    })
                    .catch((error) => {
                        reject(error)
                    })
            } catch (error) {
                reject(error)
            }
        })
    },
    getTemplates({ state, rootState, commit }) {
        return new Promise((resolve, reject) => {
            try {
                prepareParams(state.templatesConfig).then((params) => {
                    axios
                        .get(`${rootState.baseURL}/response-template`, {
                            params
                        })
                        .then((response) => {
                            commit('setTemplates', response.data.data.data)
                            commit('setPagination', response.data.data)
                            resolve(response)
                        })
                        .catch((error) => {
                            reject(error)
                        })
                })
            } catch (error) {
                reject(error)
            }
        })
    },
    getTemplatesFilters({ state, rootState, commit }, id) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/templates/filters`)
                .then((response) => {
                    commit('setTemplatesFilters', response.data)
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    postTemplate({ state, rootState, commit }, content) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${rootState.baseURL}/response-template`, content)
                .then((response) => {
                    commit('addTemplate', content)
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getTemplate({ state, rootState, commit }, id) {
        return new Promise((resolve, reject) => {
            try {
                axios
                    .get(`${rootState.baseURL}/response-template/${id}/get`)
                    .then((response) => {
                        commit('setTemplate', response.data.data)
                        resolve(response)
                    })
                    .catch((error) => {
                        reject(error)
                    })
            } catch (error) {
                reject(error)
            }
        })
    },
    editTemplate({ state, rootState, commit }, data) {
        return new Promise((resolve, reject) => {
            try {
                axios
                    .put(
                        `${rootState.baseURL}/response-template/${data.id}`,
                        data.template
                    )
                    .then((response) => {
                        commit('setTemplate', response.data.data)
                        resolve(response)
                    })
                    .catch((error) => {
                        reject(error)
                    })
            } catch (error) {
                reject(error)
            }
        })
    },
    impersonateUser({ commit, dispatch }, userId) {
        return new ImpersonateService()
            .impersonateUser(userId)
            .then((data) => {
                commit('setImpersonation', data)
                // Update auth store with impersonated user data
                commit('auth/setUser', data.user, { root: true })
                // Fetch impersonated user's role & permissions
                return dispatch('auth/fetchMe', null, { root: true })
            })
    },
    leaveImpersonate({ commit }) {
        return new ImpersonateService()
            .leaveImpersonate()
            .then(() => commit('leaveImpersonation'))
    }
}
